import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyIcon from '@mui/icons-material/VpnKey';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import type { Role, UserSummary } from 'shared';
import { api } from '../api';
import { ROLE_LABEL, useAuth } from '../auth';
import { LabeledField } from '../components/LabeledField';
import { Note } from '../components/Note';
import { formatDateTime } from '../format';
import { DISPLAY, MONO, tokens } from '../theme';

const ROLES: Role[] = [
  'WEB_ADMINISTRATOR',
  'MARKETING_EXECUTIVE',
  'CONTENT_EDITOR',
  'PRODUCT_MARKETING_LEAD',
];

const MIN_PASSWORD = 10;

function CreateUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('MARKETING_EXECUTIVE');
  const [password, setPassword] = useState('');

  const create = useMutation({
    mutationFn: () => api.createUser({ email: email.trim().toLowerCase(), name: name.trim(), role, password }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      close();
    },
  });

  function close() {
    setEmail('');
    setName('');
    setRole('MARKETING_EXECUTIVE');
    setPassword('');
    create.reset();
    onClose();
  }

  const canSubmit =
    email.trim().length > 0 &&
    name.trim().length > 0 &&
    password.length >= MIN_PASSWORD &&
    !create.isPending;

  return (
    <Dialog open={open} onClose={create.isPending ? undefined : close} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 600, color: tokens.ink }}>
        Tạo tài khoản
      </DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          spacing={2}
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) create.mutate();
          }}
        >
          <LabeledField
            label="Email"
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <LabeledField label="Tên" value={name} onChange={(e) => setName(e.target.value)} />
          <LabeledField
            label="Vai trò"
            select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            {ROLES.map((r) => (
              <MenuItem key={r} value={r} sx={{ fontSize: 14 }}>
                {ROLE_LABEL[r]}
              </MenuItem>
            ))}
          </LabeledField>
          <LabeledField
            label="Mật khẩu ban đầu"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText={`Tối thiểu ${MIN_PASSWORD} ký tự. Người dùng tự đổi sau khi đăng nhập.`}
          />
          {create.isError && <Note tone="bad">{(create.error as Error).message}</Note>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} color="inherit" disabled={create.isPending}>
          Huỷ
        </Button>
        <Button
          variant="contained"
          onClick={() => create.mutate()}
          disabled={!canSubmit}
          startIcon={create.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Tạo
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Đặt lại mật khẩu hộ — spec SC-09-C bước 1.
 *
 * Admin KHÔNG tự nghĩ mật khẩu nữa: hệ thống sinh một mật khẩu tạm và hiện đúng
 * một lần ở đây. Ba điều đổi được nhờ vậy — không còn chuyện đặt cùng một mật
 * khẩu cho mọi người, admin không biết mật khẩu lâu dài của ai, và mật khẩu tạm
 * hết hạn sau 24 giờ.
 */
function ResetPasswordDialog({
  user,
  onClose,
}: {
  user: UserSummary | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [daSaoChep, setDaSaoChep] = useState(false);

  const reset = useMutation({
    mutationFn: () => api.resetUserPassword(user!.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  function close() {
    setDaSaoChep(false);
    reset.reset();
    onClose();
  }

  const ketQua = reset.data;

  return (
    <Dialog open={!!user} onClose={reset.isPending ? undefined : close} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 600, color: tokens.ink }}>
        Đặt lại mật khẩu
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {!ketQua ? (
            <>
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
                Cho tài khoản <b>{user?.email}</b>. Hệ thống sẽ sinh một mật khẩu tạm và hiện{' '}
                <b>đúng một lần</b> ở màn này — chưa gửi được email nên bạn phải tự chuyển cho
                người dùng qua kênh khác.
              </Typography>
              <Note>
                Mật khẩu tạm hết hạn sau <b>24 giờ</b>, và người dùng bị bắt đổi ngay khi đăng
                nhập. Mọi phiên đang mở của họ bị đăng xuất.
              </Note>
              {reset.isError && <Note tone="bad">{(reset.error as Error).message}</Note>}
            </>
          ) : (
            <>
              <Note tone="warn">
                Chép ngay bây giờ. Đóng hộp thoại hoặc tải lại trang là <b>không xem lại được</b>.
              </Note>
              <Box
                sx={{
                  fontFamily: MONO,
                  fontSize: 20,
                  letterSpacing: '.06em',
                  textAlign: 'center',
                  bgcolor: tokens.line2,
                  borderRadius: '10px',
                  py: 1.8,
                  px: 1,
                  wordBreak: 'break-all',
                }}
              >
                {ketQua.tempPassword}
              </Box>
              <Button
                variant="outlined"
                onClick={() => {
                  void navigator.clipboard.writeText(ketQua.tempPassword).then(
                    () => setDaSaoChep(true),
                    () => undefined,
                  );
                }}
              >
                {daSaoChep ? 'Đã sao chép' : 'Sao chép mật khẩu tạm'}
              </Button>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                Hết hạn lúc {formatDateTime(ketQua.expiresAt)}.
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} color="inherit" disabled={reset.isPending}>
          {ketQua ? 'Đóng' : 'Huỷ'}
        </Button>
        {!ketQua && (
          <Button
            variant="contained"
            onClick={() => reset.mutate()}
            disabled={reset.isPending}
            startIcon={reset.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Sinh mật khẩu tạm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Màn quản lý người dùng — chỉ Web Administrator vào được. */
export function UsersPage() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [resetting, setResetting] = useState<UserSummary | null>(null);

  const { data, isLoading, error } = useQuery({ queryKey: ['users'], queryFn: api.listUsers });

  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: number; role?: Role; active?: boolean }) =>
      api.updateUser(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  if (me?.role !== 'WEB_ADMINISTRATOR') {
    return (
      <Note tone="bad">
        Màn này chỉ dành cho Web Administrator. Vai trò của bạn là {ROLE_LABEL[me!.role]}.
      </Note>
    );
  }

  return (
    <Box>
      {/* Đường quay lại ngay trong màn, không chỉ dựa vào thanh điều hướng: dưới
          600px thanh đó bị ẩn, và người dùng cần thoát được khỏi màn này. */}
      <Button
        component={RouterLink}
        to="/"
        startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
        size="small"
        sx={{ mb: 1.5, ml: -1, color: 'text.secondary' }}
      >
        Danh sách trang
      </Button>

      <Typography sx={{ fontSize: 11.5, letterSpacing: '.14em', color: tokens.teal, fontWeight: 600 }}>
        QUẢN TRỊ
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-end' }}
        spacing={2}
        sx={{ mt: 0.5, mb: 3 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontSize: 28, color: tokens.ink }}>
            Người dùng
          </Typography>
          <Typography sx={{ color: 'text.secondary', maxWidth: '70ch', mt: 0.5 }}>
            Bốn vai trò theo tài liệu nghiệp vụ. Hiện mọi vai trò đều xem và quét được trang; phân
            quyền chi tiết theo từng chức năng sẽ làm khi có Apply Engine.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Tạo tài khoản
        </Button>
      </Stack>

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />

      {error && <Note tone="bad">{(error as Error).message}</Note>}
      {update.isError && (
        <Box sx={{ mb: 2 }}>
          <Note tone="bad">{(update.error as Error).message}</Note>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 1.6, md: 2.2 } }}>
        {isLoading ? (
          <Stack spacing={1}>
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} variant="rounded" height={44} />
            ))}
          </Stack>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 260 }}>Người dùng</TableCell>
                  <TableCell sx={{ width: 210 }}>Vai trò</TableCell>
                  <TableCell sx={{ width: 130 }}>Tạo lúc</TableCell>
                  <TableCell align="center" sx={{ width: 110 }}>
                    Hoạt động
                  </TableCell>
                  <TableCell align="right" sx={{ width: 150 }}>
                    Mật khẩu
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((u) => {
                  const isMe = u.id === me.id;
                  return (
                    <TableRow key={u.id} sx={{ '&:nth-of-type(even) td': { bgcolor: tokens.rowAlt } }}>
                      <TableCell>
                        {/* component="div" vì bên trong có Chip (render ra <div>), mà
                            Typography mặc định render ra <p> — <div> trong <p> là HTML
                            không hợp lệ và trình duyệt sẽ tự sắp xếp lại DOM. */}
                        <Typography component="div" sx={{ fontSize: 14.5, fontWeight: 600 }}>
                          {u.name}
                          {isMe && (
                            <Chip
                              label="bạn"
                              size="small"
                              sx={{ ml: 1, height: 18, fontSize: 11, bgcolor: tokens.okBg, color: tokens.okText }}
                            />
                          )}
                        </Typography>
                        <Typography sx={{ fontFamily: MONO, fontSize: 12.5, color: 'text.secondary' }}>
                          {u.email}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {/* Không tự đổi vai trò của mình: server cũng chặn, nhưng khoá
                            ở đây để người dùng không bấm rồi mới nhận lỗi. */}
                        <Tooltip title={isMe ? 'Không tự đổi vai trò của mình' : ''}>
                          <TextField
                            select
                            size="small"
                            value={u.role}
                            disabled={isMe || update.isPending}
                            onChange={(e) => update.mutate({ id: u.id, role: e.target.value as Role })}
                            sx={{ minWidth: 190, '& .MuiInputBase-input': { fontSize: 13.5 } }}
                          >
                            {ROLES.map((r) => (
                              <MenuItem key={r} value={r} sx={{ fontSize: 14 }}>
                                {ROLE_LABEL[r]}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Tooltip>
                      </TableCell>

                      <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                        {formatDateTime(u.createdAt)}
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title={isMe ? 'Không tự khoá chính mình' : 'Khoá / mở tài khoản'}>
                          <Switch
                            size="small"
                            checked={u.active}
                            disabled={isMe || update.isPending}
                            onChange={(e) => update.mutate({ id: u.id, active: e.target.checked })}
                          />
                        </Tooltip>
                      </TableCell>

                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<KeyIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setResetting(u)}
                          sx={{ fontSize: 13 }}
                        >
                          Đặt lại
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
